import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/Notes';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import NotificationsSettingsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DashboardIcon from '@mui/icons-material/GridViewOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import AssignmentIndIcon from '@mui/icons-material/AssignmentIndOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import ViewKanbanIcon from '@mui/icons-material/ViewKanbanOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import BarChartIcon from '@mui/icons-material/InsertChartOutlinedOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import TimerIcon from '@mui/icons-material/TimerOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircleOutline';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { PermissionKey } from '@portal/shared';
import { useAuth, usePermission, useUser } from '../lib/auth';
import { Loading } from '../components/Page';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { ThemeToggle } from './colorMode';
import { BrandLogo } from '../components/BrandLogo';
import { brand, ease, tokens } from './theme';

const EXPANDED = 290;
const COLLAPSED = 90;
const SIDEBAR_KEY = 'portal.sidebar';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  perms?: PermissionKey[];
  exact?: boolean;
}

const STAFF_NAV: Array<{ section: string; items: NavItem[] }> = [
  {
    section: 'Menu',
    items: [
      { to: '/', label: 'Dashboard', icon: <DashboardIcon />, exact: true },
      { to: '/tickets', label: 'Tickets', icon: <ConfirmationNumberIcon /> },
      { to: '/my-work', label: 'My work', icon: <AssignmentIndIcon /> },
    ],
  },
  {
    section: 'Operations',
    items: [
      { to: '/products', label: 'Products & services', icon: <Inventory2Icon />, perms: ['product:view'] },
      { to: '/work-items', label: 'Development', icon: <ViewKanbanIcon />, perms: ['work:view'] },
      { to: '/clients', label: 'Clients', icon: <BusinessIcon />, perms: ['client:view', 'client:manage'] },
      { to: '/reports', label: 'Reports', icon: <BarChartIcon />, perms: ['report:view'] },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/users', label: 'Users', icon: <PeopleIcon />, perms: ['user:manage'] },
      { to: '/roles', label: 'Roles & permissions', icon: <AdminPanelSettingsIcon />, perms: ['role:manage'] },
      { to: '/admin/sla', label: 'SLA policies', icon: <TimerIcon />, perms: ['role:manage'] },
      { to: '/admin/audit', label: 'Audit log', icon: <HistoryIcon />, perms: ['audit:view'] },
    ],
  },
];

const CLIENT_NAV: Array<{ section: string; items: NavItem[] }> = [
  {
    section: 'Menu',
    items: [
      { to: '/', label: 'Home', icon: <HomeIcon />, exact: true },
      { to: '/tickets/new', label: 'Raise a ticket', icon: <AddCircleIcon />, perms: ['ticket:create'] },
      { to: '/tickets', label: 'Tickets', icon: <ConfirmationNumberIcon /> },
      { to: '/reports', label: 'Reports', icon: <BarChartIcon />, perms: ['report:view'] },
    ],
  },
  {
    section: 'Company',
    items: [{ to: '/users', label: 'Company users', icon: <PeopleIcon />, perms: ['user:manage'] }],
  },
];

const initials = (name: string) =>
  name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Header control: 44px square-ish button with a hairline border (TailAdmin `rounded-lg border`). */
const headerButton = {
  width: 44,
  height: 44,
  border: `1px solid ${tokens.line}`,
  color: tokens.muted,
  '&:hover': { bgcolor: tokens.hover, color: tokens.ink2 },
} as const;

/** Global ticket search: Enter jumps to the ticket list filtered by the query. Ctrl/⌘+K focuses it. */
function GlobalSearch() {
  const navigate = useNavigate();
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  return (
    <Box
      component="form"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        const q = value.trim();
        navigate(q ? `/tickets?q=${encodeURIComponent(q)}` : '/tickets');
        setValue('');
        ref.current?.blur();
      }}
      sx={{
        display: { xs: 'none', sm: 'flex' },
        alignItems: 'center',
        gap: 1.5,
        width: { sm: 320, lg: 430 },
        height: 44,
        pl: 2,
        pr: 1.25,
        borderRadius: 2,
        border: `1px solid ${tokens.line}`,
        bgcolor: tokens.wash,
        boxShadow: tokens.shadowXs,
        transition: `border-color .2s ${ease}, box-shadow .2s ${ease}`,
        '&:focus-within': { borderColor: brand[300], boxShadow: `0 0 0 3px rgba(70,95,255,0.1)` },
      }}
    >
      <SearchIcon sx={{ fontSize: 22, color: tokens.muted }} />
      <InputBase
        inputRef={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search tickets or type a ticket number…"
        inputProps={{ 'aria-label': 'Search tickets' }}
        sx={{ flex: 1, fontSize: 14, color: tokens.ink, '& input::placeholder': { color: tokens.subtle, opacity: 1 } }}
      />
      <Box
        component="kbd"
        sx={{
          display: { xs: 'none', md: 'inline-flex' },
          alignItems: 'center',
          fontFamily: 'inherit',
          fontSize: 12,
          letterSpacing: '-0.2px',
          color: tokens.muted,
          px: 0.875,
          py: '4.5px',
          lineHeight: 1,
          borderRadius: 2,
          border: `1px solid ${tokens.line}`,
          bgcolor: tokens.wash,
        }}
      >
        {isMac ? '⌘K' : 'Ctrl K'}
      </Box>
    </Box>
  );
}

function Logo({ compact }: { compact: boolean }) {
  return (
    <Box component={RouterLink} to="/" aria-label="Inditech Support Portal — home" sx={{ display: 'block', textDecoration: 'none', minWidth: 0 }}>
      {compact ? (
        <BrandLogo mark height={28} />
      ) : (
        <Box sx={{ animation: `ta-fade-up .25s ${ease}` }}>
          <BrandLogo height={27} />
          <Typography sx={{ mt: 0.75, fontSize: 11, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: tokens.muted, whiteSpace: 'nowrap' }}>
            Support Portal
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export function AppShell() {
  const user = useUser();
  const { logout } = useAuth();
  const can = usePermission();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [expanded, setExpanded] = useState(() => localStorage.getItem(SIDEBAR_KEY) !== 'collapsed');
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const nav = user.userType === 'staff' ? STAFF_NAV : CLIENT_NAV;
  const mainRef = useRef<HTMLElement>(null);

  // Page-enter fade on route change, animated in place so the page isn't remounted.
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    mainRef.current?.animate?.([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 350, easing: ease });
  }, [location.pathname]);

  // Desktop rail is wide when pinned open or hovered (TailAdmin behaviour); mobile always shows the full drawer.
  const wide = !desktop || expanded || hovered;
  const railWidth = expanded || hovered ? EXPANDED : COLLAPSED;

  const toggleSidebar = () => {
    if (!desktop) return setMobileOpen((o) => !o);
    setExpanded((e) => {
      localStorage.setItem(SIDEBAR_KEY, e ? 'collapsed' : 'expanded');
      return !e;
    });
    setHovered(false);
  };

  const isActive = (item: NavItem) =>
    item.exact
      ? location.pathname === item.to
      : location.pathname === item.to ||
        (location.pathname.startsWith(item.to + '/') && !(item.to === '/tickets' && location.pathname === '/tickets/new'));

  const signOut = async () => {
    setMenu(null);
    await logout();
    navigate('/login');
  };

  const sidebar = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 2.5, bgcolor: tokens.surface, overflow: 'hidden' }}>
      <Box sx={{ py: 4, display: 'flex', justifyContent: wide ? 'flex-start' : 'center', flexShrink: 0 }}>
        <Logo compact={!wide} />
      </Box>

      <Box component="nav" aria-label="Main" sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', mx: -1, px: 1, pb: 3, '&::-webkit-scrollbar': { width: 0 } }}>
        {nav.map((group) => {
          const items = group.items.filter((i) => !i.perms || can(...i.perms));
          if (!items.length) return null;
          return (
            <Box key={group.section} sx={{ mb: 3 }}>
              <Box
                sx={{
                  mb: 2,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: wide ? 'flex-start' : 'center',
                  fontSize: 12,
                  lineHeight: '20px',
                  textTransform: 'uppercase',
                  color: tokens.subtle,
                }}
              >
                {wide ? group.section : <MoreHorizIcon sx={{ fontSize: 24 }} />}
              </Box>
              <Stack component="ul" gap={0.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {items.map((item) => {
                  const active = isActive(item);
                  const link = (
                    <Box
                      component={RouterLink}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      aria-current={active ? 'page' : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: wide ? 'flex-start' : 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: '20px',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        color: active ? tokens.brandText : tokens.ink2,
                        bgcolor: active ? tokens.brandSoft : 'transparent',
                        transition: `background-color .2s ${ease}, color .2s ${ease}`,
                        '& svg': { fontSize: 24, flexShrink: 0, color: active ? tokens.brandText : tokens.muted, transition: `color .2s ${ease}` },
                        '&:hover': active ? undefined : { bgcolor: tokens.hover, '& svg': { color: tokens.ink2 } },
                      }}
                    >
                      {item.icon}
                      {wide && <span>{item.label}</span>}
                    </Box>
                  );
                  return (
                    <li key={item.to}>
                      {wide ? (
                        link
                      ) : (
                        <Tooltip title={item.label} placement="right">
                          {link}
                        </Tooltip>
                      )}
                    </li>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Box>

      {wide && (
        <Box sx={{ mb: 4, px: 2, py: 2.5, borderRadius: 4, bgcolor: tokens.wash, textAlign: 'center', flexShrink: 0, animation: `ta-fade-up .3s ${ease}` }}>
          <Typography fontWeight={600} fontSize={14} color="text.primary" noWrap>
            {user.userType === 'staff' ? 'Operations workspace' : user.organizationName}
          </Typography>
          <Typography fontSize={12.5} color="text.secondary" sx={{ mt: 0.5 }} noWrap>
            Signed in as {user.roles.join(', ') || user.email}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: tokens.canvas }}>
      {desktop ? (
        <Box
          component="aside"
          onMouseEnter={() => !expanded && setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: (t) => t.zIndex.drawer,
            height: '100vh',
            width: railWidth,
            borderRight: `1px solid ${tokens.line}`,
            transition: `width .3s ${ease}`,
          }}
        >
          {sidebar}
        </Box>
      ) : (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: EXPANDED, borderRight: `1px solid ${tokens.line}` } }}
        >
          {sidebar}
        </Drawer>
      )}

      <Box sx={{ ml: desktop ? `${railWidth}px` : 0, transition: `margin-left .3s ${ease}`, minWidth: 0 }}>
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: tokens.surface,
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <Stack direction="row" alignItems="center" gap={2} sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
            <IconButton onClick={toggleSidebar} aria-label="Toggle sidebar" sx={headerButton}>
              {!desktop && mobileOpen ? <CloseIcon /> : <MenuOpenIcon />}
            </IconButton>
            <GlobalSearch />
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" alignItems="center" gap={1.5}>
              <ThemeToggle />
              <NotificationBell />
            </Stack>
            <Box
              component="button"
              type="button"
              onClick={(e) => setMenu(e.currentTarget)}
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={!!menu}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: tokens.ink2,
                font: 'inherit',
              }}
            >
              <Avatar sx={{ width: 44, height: 44, fontSize: 15, bgcolor: brand[500], color: '#fff' }}>{initials(user.name)}</Avatar>
              <Typography component="span" fontSize={14} fontWeight={500} sx={{ display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}>
                {user.name.split(' ')[0]}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{ fontSize: 20, display: { xs: 'none', md: 'block' }, transition: `transform .2s ${ease}`, transform: menu ? 'rotate(180deg)' : 'none' }}
              />
            </Box>
            <Menu
              anchorEl={menu}
              open={!!menu}
              onClose={() => setMenu(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { width: 260, mt: 2 } } }}
            >
              <Box sx={{ px: 1, pb: 1 }}>
                <Typography fontWeight={500} fontSize={14} color={tokens.ink2} noWrap>
                  {user.name}
                </Typography>
                <Typography fontSize={12} color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                  {user.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <MenuItem
                onClick={() => {
                  setMenu(null);
                  navigate('/profile');
                }}
                sx={{ gap: 1.5 }}
              >
                <AccountCircleIcon sx={{ fontSize: 22, color: tokens.muted }} />
                Profile & security
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenu(null);
                  navigate('/notifications');
                }}
                sx={{ gap: 1.5 }}
              >
                <NotificationsSettingsIcon sx={{ fontSize: 22, color: tokens.muted }} />
                Notifications
              </MenuItem>
              <Divider sx={{ my: 1.5 }} />
              <MenuItem onClick={signOut} sx={{ gap: 1.5 }}>
                <LogoutIcon sx={{ fontSize: 22, color: tokens.muted }} />
                Sign out
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        <Box component="main" ref={mainRef} sx={{ p: { xs: 2, md: 3 }, maxWidth: 1536, mx: 'auto' }}>
          {/* Page chunks load lazily; suspend only the page, never the shell (open menus must be able to finish closing). */}
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
